import { useMemo, useCallback } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';

/**
 * Helper function to parse subsections (technologies) within a section
 * Defined outside component to be stable across renders
 *
 * Matches the parsing logic in SplitViewTechStack.js parseSubsections function
 * to ensure consistent accordion display across all versions.
 *
 * Handles both formats from the prompt:
 * - ### **Technology Name** (simple format)
 * - ### 1.1 Core Framework (numbered format from tech-stack prompt)
 */
const parseSubsectionsHelper = (content) => {
  const subsections = [];

  // Split by ### headers to get individual subsections
  // Handle both formats: ### **Name** and ### X.X Name
  const subsectionSplits = content.split(/(?=^###\s+)/gm);

  subsectionSplits.forEach((part, index) => {
    // Skip empty parts
    if (!part.trim()) {
      return;
    }

    // Try to match ### **Technology Name** format first
    let headerMatch = part.match(/^###\s+\*\*([^*]+)\*\*\s*/);
    let technologyTitle = null;
    let remainingContent = null;

    if (headerMatch) {
      // Format: ### **Technology Name**
      technologyTitle = headerMatch[1].trim();
      remainingContent = part.substring(headerMatch[0].length).trim();
    } else {
      // Try to match ### X.X Title format (e.g., ### 1.1 Core Framework)
      headerMatch = part.match(/^###\s+(?:\d+\.\d+\s+)?(.+?)$/m);
      if (headerMatch) {
        technologyTitle = headerMatch[1].trim();
        // Get content after the first line
        const lines = part.split('\n');
        remainingContent = lines.slice(1).join('\n').trim();
      } else {
        return;
      }
    }

    // Generate unique ID for this technology
    const techId = `tech-${index}-${technologyTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

    subsections.push({
      id: techId,
      title: technologyTitle,
      content: remainingContent,
      rawContent: part.trim()
    });
  });

  return subsections;
};

/**
 * Helper function to parse tech stack sections from content
 * Defined outside component to be stable across renders
 *
 * Handles multiple formats:
 * 1. "## 1. Frontend Technology Stack" with "### 1.1 Core Framework" subsections
 * 2. "## 1. Frontend Technology Stack" with "### Core Framework" subsections
 * 3. "# Technology Stack" with "### 1.1 Core Framework" subsections (flat structure)
 */
const parseTechStackContent = (content) => {
  if (!content) return [];

  const sections = [];

  // First, try to split by main sections (## Number. Title)
  const mainSectionSplits = content.split(/(?=^## \d+\.)/gm);
  const hasNumberedSections = mainSectionSplits.some(part => part.trim().match(/^##\s+\d+\./));

  if (hasNumberedSections) {
    // Standard format: ## 1. Frontend Technology Stack
    mainSectionSplits.forEach((part, index) => {
      // Skip empty parts or parts that don't start with numbered section
      if (!part.trim() || !part.match(/^##\s+\d+\./)) {
        return;
      }

      // Extract section header and content
      const lines = part.trim().split('\n');
      const headerLine = lines[0];
      const remainingContent = lines.slice(1).join('\n');

      // Parse header: "## 1. Frontend Technology Stack"
      const headerMatch = headerLine.match(/^##\s+(\d+)\.\s+(.+)$/);
      if (!headerMatch) {
        return;
      }

      const sectionNumber = headerMatch[1];
      const sectionTitle = headerMatch[2].trim();

      // Determine section type
      let sectionType = 'general';
      const titleLower = sectionTitle.toLowerCase();
      if (titleLower.includes('frontend')) sectionType = 'frontend';
      else if (titleLower.includes('backend')) sectionType = 'backend';
      else if (titleLower.includes('database')) sectionType = 'database';
      else if (titleLower.includes('devops') || titleLower.includes('infrastructure')) sectionType = 'devops';
      else if (titleLower.includes('third') || titleLower.includes('service')) sectionType = 'third-party';
      else if (titleLower.includes('development') || titleLower.includes('tool')) sectionType = 'devops';

      // Parse subsections (technologies) within this main section
      const subsections = parseSubsectionsHelper(remainingContent.trim());

      // Generate unique ID for the section
      const sectionId = `tech-section-${sectionNumber}-${sectionType}`;

      sections.push({
        id: sectionId,
        title: `${sectionNumber}. ${sectionTitle}`,
        content: remainingContent.trim(),
        type: sectionType,
        subsections: subsections
      });
    });
  } else {
    // Flat format: Content has ### subsections but no ## numbered sections
    // This happens when AI generates "# Technology Stack" followed directly by ### subsections
    // Create a single "Technology Stack" section with all subsections
    const subsections = parseSubsectionsHelper(content);

    if (subsections.length > 0) {
      // Try to extract a main title from # header
      const titleMatch = content.match(/^#\s+(.+?)$/m);
      const mainTitle = titleMatch ? titleMatch[1].trim() : 'Technology Stack';

      // Determine section type from title
      let sectionType = 'general';
      const titleLower = mainTitle.toLowerCase();
      if (titleLower.includes('frontend')) sectionType = 'frontend';
      else if (titleLower.includes('backend')) sectionType = 'backend';
      else if (titleLower.includes('database')) sectionType = 'database';
      else if (titleLower.includes('devops') || titleLower.includes('infrastructure')) sectionType = 'devops';

      sections.push({
        id: 'tech-section-1-general',
        title: mainTitle,
        content: content,
        type: sectionType,
        subsections: subsections
      });
    }
  }

  return sections;
};

/**
 * Custom hook to manage tech stack versions
 * Stores and tracks different versions of tech stack from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * 1. RHS edits not syncing to LHS (content change detection)
 * 2. Stable version IDs based on message index
 */
const useTechStackVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for tech stack version management
  const techStackConfig = {
    // Determine if a message contains tech stack
    // Simplified detection to match more content formats
    isRelevantMessage: (msg) => {
      const content = msg.content || '';

      // Check for structured data marker
      const hasStructuredData = msg.structured_data?.type === 'tech-stack';
      if (hasStructuredData) return true;

      // Simple pattern: Look for "Frontend Technology" or "Backend Technology" or "Database" section headers
      const hasTechSection = /##\s*\d*\.?\s*(Frontend|Backend|Database|DevOps|Infrastructure|Technology|Tech Stack)/i.test(content);

      // Also check for common tech stack keywords in headers
      const hasTechKeywords = /##.*(?:Stack|Framework|Architecture)/i.test(content);

      return hasTechSection || hasTechKeywords;
    },

    // Parse tech stack sections from content
    // Prefer backend-parsed sections (from ParsedResponse) over frontend parsing
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        // Backend already parsed this — add subsections if missing
        return msg.parsed_content.sections.map(s => {
          if (!s.subsections && s.content) {
            return { ...s, subsections: parseSubsectionsHelper(s.content) };
          }
          return s;
        });
      }
      return parseTechStackContent(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    // We return null for id here to let the parent hook set it properly
    extractVersions: (msg, parsedSections, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        sections: parsedSections,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the fixed version management hook (V1)
  const versionData = useVersionManagementV1(techStackConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  // Only recreate when the actual version data changes
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;

    // Ensure sections is always available - try multiple sources
    let sections = versionData.currentVersion.sections ||
                   versionData.currentVersion.parsedContent ||
                   [];

    // If sections exist but don't have subsections, re-parse from content
    // This handles cases where versions were stored before subsection parsing was added
    if (sections.length > 0 && !sections[0].subsections && versionData.currentVersion.content) {
      sections = parseTechStackContent(versionData.currentVersion.content);
    }

    // If still no sections, try parsing from content
    if (sections.length === 0 && versionData.currentVersion.content) {
      sections = parseTechStackContent(versionData.currentVersion.content);
    }

    return {
      ...versionData.currentVersion,
      sections
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to tech-stack-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useTechStackVersions;
